import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Snowflake, Flame, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiFetch } from '@/hooks/useFetch';

export const StreakFreezeNotification = () => {
  const { user, isAuthenticated } = useAuth();
  const { isSpanish } = useLanguage();
  const [showDialog, setShowDialog] = useState(false);
  const [freezeData, setFreezeData] = useState(null);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Only check once per session when user is authenticated
    if (isAuthenticated && user && !hasChecked) {
      checkAndAutoFreeze();
    }
  }, [isAuthenticated, user, hasChecked]);

  const checkAndAutoFreeze = async () => {
    try {
      const { data, error } = await apiFetch('/api/friends/streak/check-and-auto-freeze', {
        method: 'POST'
      });

      if (!error && data) {
        setFreezeData(data);
        
        // Show popup if freeze was used
        if (data.freeze_used) {
          setShowDialog(true);
        }
      }
    } catch (error) {
      console.error('Error checking streak freeze:', error);
    } finally {
      setHasChecked(true);
    }
  };

  const handleClose = () => {
    setShowDialog(false);
  };

  if (!freezeData?.freeze_used) return null;

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="sm:max-w-md" data-testid="streak-freeze-notification">
        <DialogHeader>
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Snowflake className="h-8 w-8 text-blue-500" />
          </div>
          <DialogTitle className="text-center text-xl">
            {isSpanish ? '¡Racha Salvada!' : 'Streak Saved!'}
          </DialogTitle>
          <DialogDescription className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2 text-lg font-semibold text-[#00E5CC]">
              <Flame className="h-5 w-5 text-orange-500" />
              {freezeData.current_streak} {isSpanish ? 'días' : 'days'}
            </div>
            <p>
              {isSpanish 
                ? 'Se usó automáticamente una congelación de racha porque no usaste la app ayer.'
                : 'A streak freeze was automatically used because you missed a day.'}
            </p>
            <p className="text-sm text-muted-foreground">
              {isSpanish 
                ? `Te quedan ${freezeData.freezes_available} congelaciones este mes.`
                : `You have ${freezeData.freezes_available} freezes left this month.`}
            </p>
          </DialogDescription>
        </DialogHeader>
        
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-medium">
              {isSpanish ? '¡Tu racha está protegida!' : 'Your streak is protected!'}
            </span>
          </div>
          <p className="text-sm text-green-600 dark:text-green-400 mt-1">
            {isSpanish 
              ? 'Sigue usando la app hoy para mantener tu racha activa.'
              : 'Keep using the app today to maintain your active streak.'}
          </p>
        </div>
        
        <DialogFooter>
          <Button onClick={handleClose} className="w-full bg-[#00E5CC] hover:bg-[#00D4BD] text-[#0d1f2d]">
            {isSpanish ? '¡Genial!' : 'Awesome!'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StreakFreezeNotification;
